import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Share2, Copy, Check, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import ValentineConfetti from "@/components/valentine/ValentineConfetti";
import ValentineFireworks from "@/components/valentine/ValentineFireworks";
import ImpossibleNoButton from "@/components/valentine/ImpossibleNoButton";
import ValentineCountdown from "@/components/valentine/ValentineCountdown";
import LoveLetter from "@/components/valentine/LoveLetter";
import CustomizationPanel, { ValentineSettings } from "@/components/valentine/CustomizationPanel";
import AuroraBackground from "@/components/valentine/AuroraBackground";
import FloatingParticles from "@/components/valentine/FloatingParticles";
import MusicPlayer from "@/components/valentine/MusicPlayer";
import EmojiReactions from "@/components/valentine/EmojiReactions";
import NeonText from "@/components/valentine/NeonText";

const Valentine = () => {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState(searchParams.get("name") || "");
  const [inputName, setInputName] = useState("");
  const [hasAccepted, setHasAccepted] = useState(false);
  const [noAttempts, setNoAttempts] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);

  // User customization settings
  const [settings, setSettings] = useState<ValentineSettings>({
    themeColor: "pink",
    backgroundStyle: "aurora",
    heartStyle: "mixed",
    musicTrack: "lofi",
    intensity: "normal",
    fontStyle: "neon",
    celebrationStyle: "all",
  });

  const handleYes = useCallback(() => {
    setHasAccepted(true);
    setShowCelebration(true);
  }, []);

  const handleNoAttempt = useCallback(() => {
    setNoAttempts(prev => {
      const newCount = prev + 1;
      if (newCount >= 5) {
        setTimeout(() => handleYes(), 500);
      }
      return newCount;
    });
  }, [handleYes]);

  const handleSetName = () => {
    if (inputName.trim()) {
      setName(inputName.trim());
      const url = new URL(window.location.href);
      url.searchParams.set("name", inputName.trim());
      window.history.replaceState({}, "", url.toString());
    }
  };

  const shareUrl = `${window.location.origin}/valentine${name ? `?name=${encodeURIComponent(name)}` : ""}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied! Send it to your Valentine! 💕");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Will You Be My Valentine${name ? `, ${name}` : ""}? 💕`,
          text: "Someone special wants to ask you something... 💖",
          url: shareUrl,
        });
      } catch {
        // User cancelled
      }
    } else {
      handleCopyLink();
    }
  };

  // Font style classes
  const getFontClass = () => {
    switch (settings.fontStyle) {
      case "cursive": return "font-serif italic";
      case "bold": return "font-bold";
      case "handwritten": return "font-serif";
      case "neon": return "font-bold";
      default: return "";
    }
  };

  const webAppSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Interactive Valentine's Card Generator",
    "description": "Create and share stunning interactive Valentine's cards with music, animations, and fun surprises.",
    "url": "https://thebulkimagegenerator.com/valentine",
    "applicationCategory": "EntertainmentApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  const eventSchema = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Valentine's Day 2026",
    "startDate": "2026-02-14",
    "endDate": "2026-02-14",
    "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
    "eventStatus": "https://schema.org/EventScheduled",
    "description": "Celebrate Valentine's Day with a free interactive card featuring music, animations, and surprises.",
    "location": {
      "@type": "VirtualLocation",
      "url": "https://thebulkimagegenerator.com/valentine"
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <SEO
        title={name 
          ? `Will You Be My Valentine, ${name}? | Free Interactive Card` 
          : "Will You Be My Valentine? | Free Interactive Valentine's Card 2026"}
        description={name
          ? `${name}, someone special has a Valentine's question for you! Open this interactive card with music, animations & surprises.`
          : "Send a stunning interactive Valentine's card with music, animations, and fun surprises. Free to create and share. Make February 14th unforgettable!"}
        keywords="valentine card online, be my valentine, interactive valentine card, free valentine card, valentine 2026, romantic card, love card, valentine generator, send valentine online, digital valentine"
        url={`/valentine${name ? `?name=${encodeURIComponent(name)}` : ''}`}
        jsonLd={[webAppSchema, eventSchema]}
      />

      {/* Dynamic Background */}
      <AuroraBackground 
        themeColor={settings.themeColor}
        style={settings.backgroundStyle}
        intensity={settings.intensity}
      />

      {/* Floating Particles */}
      <FloatingParticles
        heartStyle={settings.heartStyle}
        intensity={settings.intensity}
        themeColor={settings.themeColor}
      />

      {/* Emoji Reactions (click anywhere) */}
      <EmojiReactions 
        enabled={!hasAccepted}
        intensity={settings.intensity}
      />

      {/* Customization Panel */}
      <CustomizationPanel
        settings={settings}
        onSettingsChange={setSettings}
        isOpen={showSettings}
        onToggle={() => setShowSettings(!showSettings)}
      />

      {/* Music Player */}
      <MusicPlayer
        selectedTrack={settings.musicTrack}
        autoPlayCelebration={showCelebration}
        onPlayStateChange={setIsMusicPlaying}
      />

      {/* Celebration Effects */}
      <AnimatePresence>
        {showCelebration && (settings.celebrationStyle === "all" || settings.celebrationStyle === "confetti") && (
          <ValentineConfetti />
        )}
        {showCelebration && (settings.celebrationStyle === "all" || settings.celebrationStyle === "fireworks") && (
          <ValentineFireworks />
        )}
      </AnimatePresence>

      {/* Screen Flash on YES */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 bg-white z-50 pointer-events-none"
          />
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="relative z-20 min-h-screen flex flex-col items-center justify-center px-4 py-8">
        <AnimatePresence mode="wait">
          {!hasAccepted ? (
            <motion.div
              key="question"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="text-center max-w-2xl mx-auto"
            >
              {/* Pulsing Heart Icon */}
              <motion.div
                animate={{ 
                  scale: [1, 1.2, 1],
                  filter: settings.fontStyle === "neon" 
                    ? [
                        "drop-shadow(0 0 20px rgba(236,72,153,0.5))",
                        "drop-shadow(0 0 40px rgba(236,72,153,0.8))",
                        "drop-shadow(0 0 20px rgba(236,72,153,0.5))",
                      ]
                    : undefined,
                }}
                transition={{ 
                  duration: 0.8, 
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="mb-8"
              >
                <Heart className="w-24 h-24 mx-auto text-pink-500 fill-pink-500" />
              </motion.div>

              {/* Name Input (if no name set) */}
              {!name && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-8 p-6 rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 shadow-xl"
                >
                  <p className="text-pink-300 mb-4 text-lg">Enter their name to personalize:</p>
                  <div className="flex gap-3 max-w-md mx-auto">
                    <Input
                      value={inputName}
                      onChange={(e) => setInputName(e.target.value)}
                      placeholder="Their name..."
                      className="bg-white/10 border-pink-500/30 text-white placeholder:text-pink-300/50 focus:border-pink-500"
                      onKeyDown={(e) => e.key === "Enter" && handleSetName()}
                    />
                    <Button
                      onClick={handleSetName}
                      className="bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600"
                    >
                      Set
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Main Question with Neon Effect */}
              <motion.h1
                className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 leading-tight ${getFontClass()}`}
              >
                {settings.fontStyle === "neon" ? (
                  <>
                    <NeonText color={settings.themeColor} animate flickering={false}>
                      Will You Be My
                    </NeonText>
                    <br />
                    <NeonText color={settings.themeColor} animate>
                      Valentine
                      {name && <span className="text-pink-200">, {name}</span>}
                      ?
                    </NeonText>
                  </>
                ) : (
                  <>
                    <span className="bg-gradient-to-r from-pink-400 via-red-400 to-pink-400 bg-clip-text text-transparent">
                      Will You Be My
                    </span>
                    <br />
                    <span className="bg-gradient-to-r from-red-400 via-pink-500 to-red-400 bg-clip-text text-transparent">
                      Valentine
                      {name && <span className="text-pink-300">, {name}</span>}
                      ?
                    </span>
                  </>
                )}
              </motion.h1>

              {/* Emoji decorations */}
              <motion.p
                className="text-3xl mb-12"
                animate={{ 
                  scale: [1, 1.1, 1],
                }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                💕✨💖✨💕
              </motion.p>

              {/* Progress Bar */}
              {noAttempts > 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mb-8 max-w-md mx-auto"
                >
                  <p className="text-pink-300 text-sm mb-2">
                    How close are you to YES? {Math.min(99, 50 + noAttempts * 10)}%
                  </p>
                  <div className="h-3 bg-white/10 rounded-full overflow-hidden backdrop-blur-sm">
                    <motion.div
                      className="h-full bg-gradient-to-r from-pink-500 to-red-500"
                      style={{
                        boxShadow: "0 0 15px rgba(236,72,153,0.6)",
                      }}
                      initial={{ width: "50%" }}
                      animate={{ width: `${Math.min(99, 50 + noAttempts * 10)}%` }}
                    />
                  </div>
                </motion.div>
              )}

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                {/* YES Button */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    onClick={handleYes}
                    size="lg"
                    className="text-xl px-12 py-8 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-[0_0_30px_rgba(34,197,94,0.5)] hover:shadow-[0_0_50px_rgba(34,197,94,0.7)] transition-all"
                  >
                    YES 💖
                  </Button>
                </motion.div>

                {/* NO Button (The Impossible One!) */}
                <ImpossibleNoButton 
                  onAttempt={handleNoAttempt} 
                  attempts={noAttempts}
                />
              </div>

              {/* Share Section */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="mt-12 p-6 rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 shadow-xl"
              >
                <p className="text-pink-300 mb-4">Share this with someone special:</p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <Button
                    onClick={handleShare}
                    variant="outline"
                    className="border-pink-500/30 text-pink-300 hover:bg-pink-500/20"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                  <Button
                    onClick={handleCopyLink}
                    variant="outline"
                    className="border-pink-500/30 text-pink-300 hover:bg-pink-500/20"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 mr-2" />
                    ) : (
                      <Copy className="w-4 h-4 mr-2" />
                    )}
                    {copied ? "Copied!" : "Copy Link"}
                  </Button>
                  <Button
                    onClick={() => setShowQR(!showQR)}
                    variant="outline"
                    className="border-pink-500/30 text-pink-300 hover:bg-pink-500/20"
                  >
                    <QrCode className="w-4 h-4 mr-2" />
                    QR Code
                  </Button>
                </div>

                {/* QR Code Display */}
                <AnimatePresence>
                  {showQR && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4 flex justify-center"
                    >
                      <div className="p-4 bg-white rounded-xl shadow-lg">
                        <img
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(shareUrl)}`}
                          alt="QR Code"
                          className="w-[150px] h-[150px]"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              key="celebration"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center max-w-2xl mx-auto"
            >
              {/* Celebration Header */}
              <motion.div
                animate={{ 
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0],
                }}
                transition={{ duration: 0.5, repeat: 3 }}
                className="mb-8"
              >
                <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold mb-4">
                  {settings.fontStyle === "neon" ? (
                    <NeonText color={settings.themeColor}>YAY! 🎉</NeonText>
                  ) : (
                    <span className="bg-gradient-to-r from-pink-400 via-red-400 to-pink-400 bg-clip-text text-transparent">
                      YAY! 🎉
                    </span>
                  )}
                </h1>
                <p className="text-2xl sm:text-3xl text-pink-300">
                  {name ? `${name} said` : "They said"} <span className="text-green-400 font-bold">YES!</span>
                </p>
              </motion.div>

              {/* Love Letter */}
              <LoveLetter name={name} />

              {/* Countdown */}
              <ValentineCountdown />

              {/* Share Celebration */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="mt-8 p-6 rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 shadow-xl"
              >
                <p className="text-pink-300 mb-4">Share the good news! 💕</p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <Button
                    onClick={() => {
                      const text = `I said YES! 💕 Happy Valentine's Day! 🎉`;
                      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, "_blank");
                    }}
                    className="bg-[#1DA1F2] hover:bg-[#1a8cd8]"
                  >
                    Share on Twitter
                  </Button>
                  <Button
                    onClick={handleCopyLink}
                    variant="outline"
                    className="border-pink-500/30 text-pink-300 hover:bg-pink-500/20"
                  >
                    {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    {copied ? "Copied!" : "Copy Link"}
                  </Button>
                </div>
              </motion.div>

              {/* Reset Button */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2 }}
                className="mt-8"
              >
                <Button
                  onClick={() => {
                    setHasAccepted(false);
                    setShowCelebration(false);
                    setNoAttempts(0);
                  }}
                  variant="ghost"
                  className="text-pink-400/50 hover:text-pink-400"
                >
                  Ask someone else? 💌
                </Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <footer className="relative z-20 py-6 text-center text-pink-400/50 text-sm">
        Made with 💖 for Valentine's Day 2026
      </footer>
    </div>
  );
};

export default Valentine;
